// Star of the Month — full-bleed photo award card.
// The recognised employee's photo fills the card; a frosted overlay at the
// bottom carries the month chip, badge chip, name, role, reaction pills and
// carousel dots. Reacting fires a small emoji-confetti burst.
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Award } from 'lucide-react';
import { API_URL } from '../../../api/api';
import WidgetHeader from './WidgetHeader';
import { useWidgetContext } from '../WidgetContext';

const authHeaderFromStorage = () => {
  const token = localStorage.getItem('token');
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

const initialsOf = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || '')
    .join('');

const MONTH_LONG = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const resolveStarPhoto = (p) => {
  if (!p) return null;
  if (/^https?:\/\//.test(p)) return p;
  return `${API_URL}${p.startsWith('/') ? '' : '/'}${p}`;
};

const fmtStarMonth = (ym) => {
  if (!ym) return '';
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  return `${MONTH_LONG[m - 1]} ${y}`.toUpperCase();
};

const REACTION_TYPES = [
  { key: 'fire', emoji: '🔥', label: 'Appreciate' },
  { key: 'heart', emoji: '❤️', label: 'Love' },
  { key: 'thumb', emoji: '👍', label: 'Nice' },
];

// Small emoji-confetti burst rendered at the click point.
const Burst = ({ x, y, emoji }) => (
  <div className="star2-burst" style={{ left: x, top: y }} aria-hidden="true">
    {Array.from({ length: 14 }).map((_, i) => {
      const ang = (i / 14) * Math.PI * 2 + Math.random() * 0.5;
      const dist = 34 + Math.random() * 34;
      const glyph = i % 3 === 0 ? emoji : i % 3 === 1 ? '✨' : '⭐';
      return (
        <span
          key={i}
          style={{
            '--dx': `${Math.cos(ang) * dist}px`,
            '--dy': `${Math.sin(ang) * dist - 28}px`,
            '--dl': `${Math.floor(Math.random() * 120)}ms`,
            '--sc': 0.7 + Math.random() * 0.6,
          }}
        >
          {glyph}
        </span>
      );
    })}
  </div>
);

export const StarOfTheMonthWidget = () => {
  // editMode is still consumed so the widget plays nicely with Personalise,
  // but there are no per-user prefs to switch any more.
  useWidgetContext('star-of-the-month');

  const [stars, setStars] = useState([]);
  const [month, setMonth] = useState(null);
  const [isFallback, setIsFallback] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [idx, setIdx] = useState(0);
  const [isHover, setIsHover] = useState(false);
  const [reactingId, setReactingId] = useState(null);
  const [bursts, setBursts] = useState([]);

  const hostRef = useRef(null);
  const burstSeq = useRef(0);

  // Initial fetch.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API_URL}/api/star-of-month/current`,
          authHeaderFromStorage()
        );
        if (cancelled) return;
        setStars(res.data?.stars || []);
        setMonth(res.data?.month || null);
        setIsFallback(!!res.data?.is_fallback);
        setIdx(0);
      } catch (e) {
        if (!cancelled) {
          console.warn('[star-widget] fetch failed', e?.message);
          setErr('Could not load stars.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-advance every 5s; pause on hover.
  useEffect(() => {
    if (stars.length <= 1 || isHover) return undefined;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % stars.length);
    }, 5000);
    return () => clearInterval(t);
  }, [stars.length, isHover]);

  const go = (next) => {
    if (!stars.length) return;
    setIdx(((next % stars.length) + stars.length) % stars.length);
  };

  const fireBurst = (e, emoji) => {
    const host = hostRef.current;
    if (!host) return;
    const rect = host.getBoundingClientRect();
    const id = ++burstSeq.current;
    setBursts((b) => [
      ...b,
      { id, x: e.clientX - rect.left, y: e.clientY - rect.top, emoji },
    ]);
    setTimeout(() => setBursts((b) => b.filter((p) => p.id !== id)), 1100);
  };

  const onReact = async (e, star, type) => {
    if (reactingId) return;
    setReactingId(star.id);
    const nextType = star.my_reaction === type ? null : type;
    if (nextType) {
      const emoji = REACTION_TYPES.find((r) => r.key === type)?.emoji || '⭐';
      fireBurst(e, emoji);
    }
    // Optimistic counts.
    setStars((prev) =>
      prev.map((s) => {
        if (s.id !== star.id) return s;
        const delta = (key, op) => ({
          [`${key}_count`]: Math.max(0, (s[`${key}_count`] || 0) + op),
        });
        let patch = { ...s };
        if (s.my_reaction) {
          patch = { ...patch, ...delta(s.my_reaction, -1) };
        }
        if (nextType) {
          patch = { ...patch, ...delta(nextType, +1) };
        }
        patch.my_reaction = nextType;
        return patch;
      })
    );
    try {
      const res = await axios.post(
        `${API_URL}/api/star-of-month/${star.id}/reaction`,
        { type: nextType },
        authHeaderFromStorage()
      );
      setStars((prev) =>
        prev.map((s) =>
          s.id === star.id
            ? {
                ...s,
                fire_count: res.data?.fire_count ?? s.fire_count,
                heart_count: res.data?.heart_count ?? s.heart_count,
                thumb_count: res.data?.thumb_count ?? s.thumb_count,
                my_reaction: res.data?.my_reaction ?? null,
              }
            : s
        )
      );
    } catch (e2) {
      console.warn('[star-widget] react failed', e2?.message);
      try {
        const r2 = await axios.get(
          `${API_URL}/api/star-of-month/current`,
          authHeaderFromStorage()
        );
        setStars(r2.data?.stars || []);
      } catch {}
    } finally {
      setReactingId(null);
    }
  };

  if (loading || err || !stars.length) {
    return (
      <div className="star2-host" ref={hostRef}>
        <WidgetHeader icon={Award} title="Star of the Month" />
        <div className="star2-empty">
          {loading ? (
            'Loading…'
          ) : err ? (
            err
          ) : (
            <>
              <div className="star2-empty-title">No stars set yet</div>
              <div className="star2-empty-sub">
                Admins &amp; HR can add stars under Create → Star of the Month.
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  const current = stars[idx] || stars[0];
  const avatarUrl =
    resolveStarPhoto(current.photo_override) ||
    resolveStarPhoto(current.profile_photo);
  const monthLabel = fmtStarMonth(month) + (isFallback ? ' · LATEST' : '');
  const badgeLabel =
    current.badge === 'Other'
      ? current.badge_custom || 'Recognition'
      : current.badge;

  return (
    <div
      className="star2-host"
      ref={hostRef}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      <WidgetHeader icon={Award} title="Star of the Month" />

      <div
        className={`star2-hero${avatarUrl ? '' : ' star2-hero--fallback'}`}
        style={avatarUrl ? { backgroundImage: `url(${avatarUrl})` } : undefined}
      >
        {!avatarUrl && (
          <span className="star2-hero-initials" aria-hidden="true">
            {initialsOf(current.name)}
          </span>
        )}

        {stars.length > 1 && (
          <>
            <button
              type="button"
              className="star2-nav star2-nav--prev"
              aria-label="Previous star"
              onClick={() => go(idx - 1)}
            >
              ‹
            </button>
            <button
              type="button"
              className="star2-nav star2-nav--next"
              aria-label="Next star"
              onClick={() => go(idx + 1)}
            >
              ›
            </button>
          </>
        )}

        <div className="star2-overlay">
          <div className="star2-chips">
            <span className="star2-chip star2-chip--month">{monthLabel}</span>
            {badgeLabel && (
              <span className="star2-chip star2-chip--badge">
                <i className="star2-chip-dot" aria-hidden="true" />
                {badgeLabel}
              </span>
            )}
          </div>

          <div className="star2-id">
            <h4 className="star2-name">{current.name}</h4>
            <p className="star2-role">
              {current.designation || current.department || ''}
            </p>
          </div>

          <div className="star2-reactions" role="group" aria-label="React">
            {REACTION_TYPES.map((r) => {
              const count = current[`${r.key}_count`] || 0;
              const active = current.my_reaction === r.key;
              return (
                <button
                  key={r.key}
                  type="button"
                  className={`star2-react${active ? ' is-active' : ''}`}
                  onClick={(e) => onReact(e, current, r.key)}
                  disabled={reactingId === current.id}
                  aria-pressed={active}
                  aria-label={`${r.label} (${count})`}
                  title={r.label}
                >
                  <span aria-hidden="true">{r.emoji}</span>
                  <span className="star2-react-count">{count}</span>
                </button>
              );
            })}
          </div>

          {stars.length > 1 && (
            <span className="star2-dots" aria-hidden="true">
              {stars.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  className={`star2-dot${i === idx ? ' is-on' : ''}`}
                  onClick={() => go(i)}
                  aria-label={`Go to star ${i + 1}`}
                />
              ))}
            </span>
          )}
        </div>
      </div>

      {bursts.map((b) => (
        <Burst key={b.id} x={b.x} y={b.y} emoji={b.emoji} />
      ))}
    </div>
  );
};

export default StarOfTheMonthWidget;
